import type { ComparisonData } from "../types";

export const klingComparison: ComparisonData = {
  slug: "kling-vs-todo-ai-text-to-video",
  competitorName: "Kling AI",
  competitorCategory: "AI Text-to-Video Generator",
  todoAiFlowPath: "/text-to-video",
  title: "Kling AI vs TodoAI Text-to-Video — Which AI Video Generator Wins?",
  metaDescription:
    "Compare Kling AI and TodoAI's Text-to-Video in 2026: Hebrew support, pricing in shekels, ease of use, output quality, and which is better for solo creators. Updated May 2026.",

  introTodoAi:
    "TodoAI's Text-to-Video flow turns a Hebrew or English description into a short AI-generated clip — no model picker, no parameter tuning. The same account also covers image generation, product photos, talking avatars, and an embeddable AI chatbot. Built and operated in Israel; pricing in shekels with annual billing options.",

  introCompetitor:
    "Kling AI is a Chinese-developed text-to-video model (Kuaishou) known for fluid camera motion and physics-realistic output. It's available globally via a web app, supports text + image-to-video, and has free credits to test. The product is English/Chinese first, with no native Hebrew UI; pricing is in USD and oriented toward power users.",

  rows: [
    {
      label: "Free credits to try",
      todoAi: "100 credits/month — enough for ~3 short videos",
      competitor: "166 free credits at signup, then daily refill",
      highlight: "tie",
    },
    {
      label: "Starting paid price",
      todoAi: "₪35 / ~$10 per month (Starter)",
      competitor: "$8/month (Standard), $36/month (Pro)",
      highlight: "tie",
    },
    {
      label: "Hebrew prompt support",
      todoAi: "Yes — Hebrew accepted natively, UI in Hebrew",
      competitor: "Limited — primarily English/Chinese",
      highlight: "todoAi",
    },
    {
      label: "Hebrew UI / RTL",
      todoAi: "Yes — full RTL layout",
      competitor: "No",
      highlight: "todoAi",
    },
    {
      label: "Max video length (consumer plan)",
      todoAi: "Short clips (5-10s typical)",
      competitor: "Up to 10s on Standard; longer on Pro",
      highlight: "competitor",
    },
    {
      label: "Image-to-video",
      todoAi: "Yes — separate flow (Animate a Photo)",
      competitor: "Yes — same model handles both",
      highlight: "tie",
    },
    {
      label: "Additional flows on the same account",
      todoAi: "9 flows: image gen, product shots, profile photo, talking avatar, embeddable chatbot, etc.",
      competitor: "Video only",
      highlight: "todoAi",
    },
    {
      label: "Pricing currency",
      todoAi: "ILS (shekels) — VAT-friendly for Israeli businesses",
      competitor: "USD only — currency-conversion cost on each charge",
      highlight: "todoAi",
    },
    {
      label: "Best for",
      todoAi: "Hebrew-speaking creators wanting one tool for image, video, and chatbot work",
      competitor: "Specialists who want the best raw video model with maximum length",
      highlight: "tie",
    },
  ],

  stats: [
    {
      value: "₪35/mo",
      description: "TodoAI Starter price in shekels — the lowest-priced Hebrew-native text-to-video plan",
    },
    {
      value: "Yes",
      description: "Hebrew prompt and UI support in TodoAI's Text-to-Video flow",
    },
    {
      value: "166 credits",
      description: "Kling AI free signup bonus credits",
      source: "https://klingai.com",
      sourceLabel: "Kling AI",
    },
    {
      value: "1 account",
      description: "Number of accounts needed on TodoAI to cover image, video, talking avatar, product shots, and an embeddable chatbot",
    },
  ],

  faqs: [
    {
      question: "Is Kling AI or TodoAI better for Hebrew text-to-video?",
      answer:
        "TodoAI is built Hebrew-first, with full Hebrew UI, RTL layout, and Hebrew-aware prompt parsing. Kling AI accepts Hebrew strings but is optimized for English and Chinese; the UI is not localized to Hebrew. For Hebrew creators, TodoAI is the more native experience.",
    },
    {
      question: "Can I get longer videos on Kling?",
      answer:
        "Yes — Kling's Pro plan supports longer clip durations than TodoAI's consumer tiers. If your primary need is long-form AI video, Kling is currently stronger. For most short-form creator content (~5-10s), both produce comparable output.",
    },
    {
      question: "Does TodoAI use Kling under the hood?",
      answer:
        "TodoAI selects the best available model per flow and may use Kling, Veo, or other providers behind the scenes — but the user never picks a model or tunes parameters. The whole point of TodoAI's design is to remove model-picking from the workflow.",
    },
    {
      question: "Which is more affordable for an Israeli small business?",
      answer:
        "TodoAI prices in shekels (₪35/month Starter) and includes nine flows on the same account. Kling prices in USD ($8-36/month) and offers only video. For a small business that also needs product photos, profile pictures, and an embeddable chatbot, TodoAI is materially cheaper end-to-end.",
    },
    {
      question: "Can I try both for free?",
      answer:
        "Yes — TodoAI gives 100 credits/month free, no card required. Kling gives 166 credits on signup plus a daily refill. Both let you generate at least 2-3 short videos before paying.",
    },
  ],

  lastUpdated: "2026-05-26",
};
